import React, { memo, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import ImageGallery from "./ImagePreview";
import { fetchImagePreviews, uploadFile } from "./api";
import RequiredTitle from "./RequiredTitle";

const UploadFileField = ({ input, control, error, defaultValue, setValue }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState([]);
  const {
    name,
    type,
    label,
    validation,
    alignment = {},
    defaultValues = defaultValue,
    isPreviewImage = false,
    isMultiple = true,
    min,
    ...rest
  } = input;
  const { sm = 12, md = 12, lg = 12 } = alignment;

  const onSubmit = async (files, setValue, existingFiles = []) => {
    try {
      setIsUploading(true);

      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append("files", file);
      });

      const uploadRes = await uploadFile(formData);

      if (uploadRes) {
        const newFileArr = [];
        Array.from(uploadRes).forEach((file) => {
          newFileArr.push(file?.fileName);
        });
        if (isMultiple) {
          setValue([...existingFiles, ...newFileArr]);
        } else {
          setValue(newFileArr);
        }
        if (isPreviewImage) {
          const imageArr = await fetchImagePreviews(newFileArr);
          if (isMultiple) {
            setImages((prev) => [...prev, ...imageArr]);
          } else {
            setImages(imageArr);
          }
        }
      }
    } catch (error) {
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const fetchImages = async () => {
      const imgArr = typeof defaultValues === 'string' ? defaultValues.split(",") : defaultValues;
      if (Array.isArray(imgArr) && imgArr.length > 0) {
        const imagesData = await fetchImagePreviews(imgArr);
        setImages(imagesData);
      } else {
        setImages([]);
      }
    };
    if (defaultValues && isPreviewImage) {
      fetchImages();
    }
  }, [defaultValues]);

  const changeImageOrder = (imageData) => {
    const imageLists = imageData?.map((row) => row.fileName);
    setValue(name, imageLists);
    setImages(imageData);
  }

  return (
    <div
      key={name}
      className={`form-group mb-3 col-sm-${sm} col-md-${md} col-lg-${lg}`}
    >
      <RequiredTitle
        label={label}
        validation={validation}
      />
      <Controller
        name={name}
        control={control}
        rules={{
          ...validation,
          validate: (value) => {
            const fileCount = Array.isArray(value) ? value.length : 0;
            if (min && fileCount < min) {
              return `Please upload at least ${min} image${min > 1 ? "s" : ""}.`;
            }
            return true;
          },
        }}
        disabled={isUploading || false}
        defaultValue={defaultValues}
        render={({ field }) => (
          <div className="input-container">
            {isUploading ? <div className="loader"></div> : null}

            <input
              id={name}
              type="file"
              {...rest}
              multiple={isMultiple}
              onChange={(e) => onSubmit(e.target.files, field.onChange, field.value || [])}
              className={`form-control ${error ? "is-invalid" : ""}`}
            />

            {error && <div className="invalid-feedback">{error}</div>}
          </div>
        )}
      />
      {isPreviewImage && <ImageGallery images={images} changeImageOrder={changeImageOrder} />}
    </div>
  );
};

export default memo(UploadFileField);
